/** Client-side mirror of server intune compliance logic (dashboard + asset filtering). */

export const INTUNE_COMPLIANCE_TYPES = {
  conditionalAccess: 'Device compliance policies',
  encryption: 'Encryption',
  antiMalware: 'Anti-Malware'
};

const INCLUDED_CHASSIS = new Set(['computer', 'mobile']);

function getObjectProperty(obj, key) {
  if (!obj || typeof obj !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const lower = key.toLowerCase();
  const match = Object.keys(obj).find((k) => k.toLowerCase() === lower);
  return match != null ? obj[match] : undefined;
}

function getIntuneRawValue(intuneData, pathSegments) {
  if (!intuneData || typeof intuneData !== 'object') return undefined;
  let current = intuneData;
  for (const segment of pathSegments) {
    current = getObjectProperty(current, segment);
    if (current === undefined) return undefined;
  }
  return current;
}

function pickIntuneRawValue(intuneData, candidatePaths) {
  for (const path of candidatePaths) {
    const value = getIntuneRawValue(intuneData, path);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function isTruthyJsonBool(value) {
  if (value == null) return false;
  return value === true || value === 1 || String(value).toLowerCase() === 'true';
}

export function parseIntuneRawColumn(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function getAssetChassisType(asset) {
  return asset?.ChassisType ?? asset?.chassistype ?? asset?.chassisType ?? asset?.type ?? null;
}

export function getAssetImei(asset) {
  const intuneData = parseIntuneRawColumn(asset?.['intune-raw']);
  const imei = pickIntuneRawValue(intuneData, [
    ['AdditionalProperties', 'Imei'],
    ['AdditionalProperties', 'IMEI'],
    ['Imei'],
    ['IMEI']
  ]);
  if (imei == null || imei === '') return null;
  return String(imei);
}

function normalizeScalarText(value) {
  const scalar = unwrapScalar(value);
  if (scalar == null || scalar === '' || typeof scalar === 'object') return null;
  const text = String(scalar).trim();
  return text || null;
}

/** Trailing build from a Datto operatingSystem string, e.g. "Microsoft Windows 11 Enterprise 10.0.26200". */
export function extractOperatingSystemVersion(operatingSystem) {
  const text = normalizeScalarText(operatingSystem);
  if (!text) return null;
  const lastPart = text.split(/\s+/).pop();
  if (lastPart && /^\d+(?:\.\d+)+$/.test(lastPart)) return lastPart;
  const match = text.match(/(\d+(?:\.\d+)+)\s*$/);
  return match ? match[1] : null;
}

function getIntuneOsVersion(intuneData) {
  return normalizeScalarText(pickIntuneRawValue(intuneData, [
    ['osVersion'],
    ['AdditionalProperties', 'osVersion']
  ]));
}

function getDattoOperatingSystem(dattoData) {
  if (!dattoData || typeof dattoData !== 'object') return null;
  const basic = getDattoBasicInfo(dattoData);
  const fromBasic = basic ? getObjectProperty(basic, 'operatingSystem') : undefined;
  return normalizeScalarText(fromBasic ?? getObjectProperty(dattoData, 'operatingSystem'));
}

export function getAssetOsVersion(asset) {
  const intuneVersion = getIntuneOsVersion(parseIntuneRawColumn(asset?.['intune-raw']));
  if (intuneVersion) return intuneVersion;
  return extractOperatingSystemVersion(getDattoOperatingSystem(parseDattoRawColumn(asset?.['datto-raw'])));
}

export function isExcludedIntuneComplianceChassisType(chassisType) {
  if (chassisType == null || String(chassisType).trim() === '') return true;
  return !INCLUDED_CHASSIS.has(String(chassisType).trim().toLowerCase());
}

export function isConditionalAccessCompliant(intuneData) {
  if (!intuneData) return false;
  const state = pickIntuneRawValue(intuneData, [
    ['AdditionalProperties', 'ComplianceState'],
    ['Status'],
    ['ComplianceState']
  ]);
  if (state == null || state === '') return false;
  return String(state).toLowerCase() === 'compliant';
}

function equalsIgnoreCaseTrim(value, expected) {
  if (value == null || expected == null) return false;
  return String(value).trim().toLowerCase() === String(expected).trim().toLowerCase();
}

function getDattoBasicInfo(dattoData) {
  if (!dattoData || typeof dattoData !== 'object') return null;
  return getObjectProperty(dattoData, 'BasicInfo')
    ?? getObjectProperty(dattoData, 'Basisinfo')
    ?? dattoData;
}

function unwrapScalar(value, nestedKeys = ['value']) {
  if (value == null || typeof value !== 'object') return value;
  for (const key of nestedKeys) {
    const nested = getObjectProperty(value, key);
    if (nested !== undefined && nested !== null && nested !== '') return nested;
  }
  return value;
}

export function parseDattoRawColumn(raw) {
  return parseIntuneRawColumn(raw);
}

function parseTimestampMs(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value <= 0) return null;
    return value < 1e12 ? value * 1000 : value;
  }
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d+(\.\d+)?$/.test(text)) {
    const numeric = Number(text);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    return numeric < 1e12 ? numeric * 1000 : numeric;
  }
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
}

function getIntuneLastSyncMs(intuneData) {
  return parseTimestampMs(pickIntuneRawValue(intuneData, [
    ['lastSyncDateTime'],
    ['LastSyncDateTime'],
    ['AdditionalProperties', 'lastSyncDateTime'],
    ['AdditionalProperties', 'LastSyncDateTime']
  ]));
}

function getDattoLastSeenMs(dattoData) {
  const basic = getDattoBasicInfo(dattoData);
  const lastSeen = (basic ? getObjectProperty(basic, 'lastSeen') : undefined)
    ?? pickIntuneRawValue(dattoData, [
      ['lastSeen'],
      ['BasicInfo', 'lastSeen'],
      ['Basisinfo', 'lastSeen']
    ]);
  return parseTimestampMs(lastSeen);
}

function pickNewestComplianceSource(intuneData, dattoData) {
  const hasIntune = Boolean(intuneData);
  const hasDatto = Boolean(dattoData);
  if (!hasIntune && !hasDatto) return null;
  if (hasIntune && !hasDatto) return 'intune';
  if (hasDatto && !hasIntune) return 'datto';

  const intuneMs = getIntuneLastSyncMs(intuneData);
  const dattoMs = getDattoLastSeenMs(dattoData);
  if (intuneMs != null && dattoMs != null) {
    return dattoMs > intuneMs ? 'datto' : 'intune';
  }
  if (dattoMs != null) return 'datto';
  if (intuneMs != null) return 'intune';
  return 'intune';
}

function isIntuneEncryptionCompliant(intuneData) {
  if (!intuneData) return false;
  const encrypted = pickIntuneRawValue(intuneData, [
    ['AdditionalProperties', 'IsEncrypted'],
    ['IsEncrypted']
  ]);
  return isTruthyJsonBool(encrypted);
}

function isDattoEncryptionCompliant(dattoData) {
  const basic = getDattoBasicInfo(dattoData);
  if (!basic) return false;
  const udf = getObjectProperty(basic, 'udf');
  const udf10 = unwrapScalar(
    (udf && typeof udf === 'object' ? getObjectProperty(udf, 'udf10') : undefined)
      ?? getObjectProperty(basic, 'udf10')
  );
  return equalsIgnoreCaseTrim(udf10, 'Encrypted');
}

export function isEncryptionCompliant(intuneData, dattoData) {
  const source = pickNewestComplianceSource(intuneData, dattoData);
  if (source === 'datto') return isDattoEncryptionCompliant(dattoData);
  if (source === 'intune') return isIntuneEncryptionCompliant(intuneData);
  return false;
}

function isIntuneAntiMalwareCompliant(intuneData) {
  if (!intuneData) return false;
  const malwareEnabled = pickIntuneRawValue(intuneData, [
    ['WindowsProtectionState', 'malwareProtectionEnabled'],
    ['AdditionalProperties', 'WindowsProtectionState', 'malwareProtectionEnabled'],
    ['AdditionalProperties', 'malwareProtectionEnabled'],
    ['malwareProtectionEnabled']
  ]);
  const realTimeEnabled = pickIntuneRawValue(intuneData, [
    ['WindowsProtectionState', 'realTimeProtectionEnabled'],
    ['AdditionalProperties', 'WindowsProtectionState', 'realTimeProtectionEnabled'],
    ['AdditionalProperties', 'realTimeProtectionEnabled'],
    ['realTimeProtectionEnabled']
  ]);
  return isTruthyJsonBool(malwareEnabled) && isTruthyJsonBool(realTimeEnabled);
}

function collectDattoAntivirusStatuses(dattoData) {
  const basic = getDattoBasicInfo(dattoData);
  if (!basic) return [];
  const antivirus = getObjectProperty(basic, 'antivirus')
    ?? getObjectProperty(dattoData, 'antivirus');
  const statuses = [];
  const pushStatus = (value) => {
    const status = unwrapScalar(
      typeof value === 'object' && value != null
        ? (getObjectProperty(value, 'antivirusStatus') ?? getObjectProperty(value, 'status') ?? value)
        : value,
      ['antivirusStatus', 'status']
    );
    if (status != null && status !== '' && typeof status !== 'object') statuses.push(status);
  };
  if (antivirus == null) {
    pushStatus(getObjectProperty(basic, 'antivirusStatus'));
  } else if (Array.isArray(antivirus)) {
    antivirus.forEach(pushStatus);
  } else {
    pushStatus(antivirus);
  }
  return statuses;
}

function isDattoAntiMalwareCompliant(dattoData) {
  return collectDattoAntivirusStatuses(dattoData).some((status) =>
    equalsIgnoreCaseTrim(status, 'RunningAndUpToDate')
  );
}

export function isAntiMalwareCompliant(intuneData, dattoData) {
  const source = pickNewestComplianceSource(intuneData, dattoData);
  if (source === 'datto') return isDattoAntiMalwareCompliant(dattoData);
  if (source === 'intune') return isIntuneAntiMalwareCompliant(intuneData);
  return false;
}

export function isAssetIntuneComplianceCompliant(asset, complianceType) {
  const intuneData = parseIntuneRawColumn(asset['intune-raw']);
  const dattoData = parseDattoRawColumn(asset['datto-raw']);
  switch (complianceType) {
    case 'conditionalAccess':
      return isConditionalAccessCompliant(intuneData);
    case 'encryption':
      return isEncryptionCompliant(intuneData, dattoData);
    case 'antiMalware':
      return isAntiMalwareCompliant(intuneData, dattoData);
    default:
      return true;
  }
}

export function isAssetActiveForIntuneCompliance(asset) {
  const active = asset?.isActive ?? asset?.isactive;
  return active === 1 || active === true;
}

/** Match dashboard scope: active devices excluding certain chassis types. */
export function isAssetInIntuneComplianceScope(asset) {
  if (!isAssetActiveForIntuneCompliance(asset)) return false;
  return !isExcludedIntuneComplianceChassisType(getAssetChassisType(asset));
}

export function assetMatchesIntuneComplianceFilter(asset, complianceType, status = 'nonCompliant') {
  if (!complianceType || !INTUNE_COMPLIANCE_TYPES[complianceType]) return true;
  if (!isAssetInIntuneComplianceScope(asset)) return false;
  const compliant = isAssetIntuneComplianceCompliant(asset, complianceType);
  return status === 'nonCompliant' ? !compliant : compliant;
}

export function getIntuneComplianceFilterLabel(complianceType) {
  const name = INTUNE_COMPLIANCE_TYPES[complianceType] || complianceType;
  return `Not compliant: ${name}`;
}

const DEVICE_COMPLIANCE_POLICY_PATHS = [
  ['deviceCompliancePolicies'],
  ['deviceCompliancePolicyStates'],
  ['AdditionalProperties', 'deviceCompliancePolicies'],
  ['AdditionalProperties', 'deviceCompliancePolicyStates']
];

function normalizeIntunePolicyCollection(value) {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item != null && typeof item === 'object');
  }
  if (typeof value === 'object') {
    const displayName = getObjectProperty(value, 'displayName') ?? getObjectProperty(value, 'name');
    const state = getObjectProperty(value, 'state');
    if (displayName != null || state != null) {
      return [value];
    }
    return Object.keys(value)
      .sort((a, b) => {
        const numA = Number(a);
        const numB = Number(b);
        if (Number.isFinite(numA) && Number.isFinite(numB)) return numA - numB;
        return String(a).localeCompare(String(b));
      })
      .map((key) => value[key])
      .filter((item) => item != null && typeof item === 'object');
  }
  return [];
}

function getPolicyDisplayName(item) {
  const displayName = getObjectProperty(item, 'displayName') ?? getObjectProperty(item, 'name');
  if (displayName == null || String(displayName).trim() === '') return null;
  return String(displayName).trim();
}

function getDeviceCompliancePolicyItems(intuneData) {
  if (!intuneData) return [];

  const seen = new Map();
  for (const path of DEVICE_COMPLIANCE_POLICY_PATHS) {
    const raw = getIntuneRawValue(intuneData, path);
    const items = normalizeIntunePolicyCollection(raw);
    for (const item of items) {
      const displayName = getPolicyDisplayName(item);
      if (!displayName) continue;

      const state = getObjectProperty(item, 'state');
      const normalizedState = state != null && String(state).trim() !== ''
        ? String(state).trim()
        : null;
      const key = displayName.toLowerCase();
      const existing = seen.get(key);

      if (!existing) {
        seen.set(key, {
          displayName,
          state: normalizedState || 'Unknown'
        });
        continue;
      }

      if (existing.state === 'Unknown' && normalizedState) {
        seen.set(key, { displayName, state: normalizedState });
      }
    }
  }

  return Array.from(seen.values());
}

export function getDeviceCompliancePolicyStates(asset) {
  const intuneData = parseIntuneRawColumn(asset?.['intune-raw']);
  return getDeviceCompliancePolicyItems(intuneData);
}

export function getDeviceCompliancePolicyNames(intuneData) {
  return getDeviceCompliancePolicyItems(intuneData).map((policy) => policy.displayName);
}

export function formatDeviceCompliancePolicyTooltip(policies) {
  if (!policies?.length) return '';
  return policies.map((policy) => `${policy.displayName}: ${policy.state}`).join('\n');
}
